import { toast } from '@osteopro/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth-provider.js';

/**
 * Déconnexion automatique après une heure sans activité.
 *
 * Comportement de la V1 (`hooks/useInactivityLogout.ts`) que le portage avait perdu, et qui n'est
 * pas un confort : un écran de cabinet reste ouvert sur un dossier patient entre deux
 * consultations, en salle d'attente, sur un poste partagé. Le serveur fait expirer la session,
 * mais l'écran, lui, continuait d'afficher un dossier de santé indéfiniment.
 *
 * La déconnexion est **silencieuse pour l'état, bruyante pour l'utilisateur** : `logout()` suffit
 * à faire retomber la garde d'accès sur `/login`, et un message dit pourquoi — sans quoi le
 * praticien croit à une panne et rouvre une session pour rien.
 *
 * Les événements sont écoutés en phase de capture et sur `document` : un composant qui arrête la
 * propagation d'un clic ne doit pas passer pour de l'inactivité.
 */

export const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

/** On ne réarme pas la minuterie à chaque pixel de défilement : une fois toutes les cinq secondes. */
const THROTTLE_MS = 5_000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'] as const;

export function useInactivityLogout(enabled: boolean): void {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReset = useRef(0);
  // Les rappels changent à chaque rendu ; la minuterie, elle, ne doit pas être réarmée pour ça.
  const expire = useRef<() => void>(() => undefined);

  useEffect(() => {
    expire.current = () => {
      toast({
        title: t('auth:session.expired_title'),
        description: t('auth:session.expired_body'),
      });
      void logout();
    };
  }, [logout, t]);

  useEffect(() => {
    if (!enabled) return;

    const arm = () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        expire.current();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset.current < THROTTLE_MS) return;
      lastReset.current = now;
      arm();
    };

    arm();
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { capture: true, passive: true });
    }
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity, { capture: true });
      }
    };
  }, [enabled]);
}
